import React, { useState, useMemo, useEffect, useCallback } from "react";
import type { DiffWBSNode } from "../../../models";
import { useDiff } from "./useDiff";
import DiffTreeNode from "../DiffTreeNode/DiffTreeNode";
import DiffSidebar from "../DiffSidebar/DiffSidebar";
import RevertAllModal from "../RevertAllModal/RevertAllModal";
import { formatDataDate } from "../../../utils/format";
import "./DiffView.css";

/* ── helpers ─────────────────────────────────────────────── */

function collectAllPaths(nodes: DiffWBSNode[]): Set<string> {
  const paths = new Set<string>();
  function walk(n: DiffWBSNode) {
    paths.add(n.wbs_path);
    n.children.forEach(walk);
  }
  nodes.forEach(walk);
  return paths;
}

function buildParentMap(nodes: DiffWBSNode[]): Map<string, string> {
  const map = new Map<string, string>();
  function walk(n: DiffWBSNode) {
    n.children.forEach((child) => {
      map.set(child.wbs_path, n.wbs_path);
      walk(child);
    });
  }
  nodes.forEach(walk);
  return map;
}

function collectDescendantPaths(node: DiffWBSNode): string[] {
  const paths: string[] = [];
  function walk(n: DiffWBSNode) {
    paths.push(n.wbs_path);
    n.children.forEach(walk);
  }
  walk(node);
  return paths;
}

/* ── component ───────────────────────────────────────────── */

const DiffView: React.FC = () => {
  const {
    versions, oldVer, newVer, setOldVer, setNewVer,
    diff, loading, error,
    canRevert, reverting, revertField, revertActivity,
    revertingAll, revertAll,
  } = useDiff();

  const [showRevertAllModal, setShowRevertAllModal] = useState(false);

  const allPaths = useMemo(
    () => (diff ? collectAllPaths(diff.roots) : new Set<string>()),
    [diff]
  );

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(allPaths);

  // Re-initialize when diff changes (new version selected)
  useEffect(() => {
    setExpandedPaths(allPaths);
  }, [allPaths]);

  const parentMap = useMemo(
    () => (diff ? buildParentMap(diff.roots) : new Map<string, string>()),
    [diff]
  );

  const allExpanded = diff ? expandedPaths.size >= allPaths.size : true;

  const handleToggle = useCallback((wbsPath: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(wbsPath)) {
        next.delete(wbsPath);
      } else {
        next.add(wbsPath);
      }
      return next;
    });
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandedPaths(new Set());
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedPaths(allPaths);
  }, [allPaths]);

  const handleNavigate = useCallback((node: DiffWBSNode) => {
    if (!diff) return;

    // Collect ancestors
    const ancestors: string[] = [];
    let cur = node.wbs_path;
    while (parentMap.has(cur)) {
      cur = parentMap.get(cur)!;
      ancestors.push(cur);
    }

    // Collect target + all descendants
    const descendants = collectDescendantPaths(node);

    setExpandedPaths((prev) => {
      const next = new Set(prev);
      ancestors.forEach((p) => next.add(p));
      descendants.forEach((p) => next.add(p));
      return next;
    });

    // Wait for React to render expanded ancestors, then scroll
    setTimeout(() => {
      const el = document.querySelector(`[data-wbs-path="${node.wbs_path}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, [diff, parentMap]);

  return (
    <div className="diff-view">
      <div className="diff-toolbar">
        <label className="diff-picker">
          <span>Old</span>
          <select
            value={oldVer ?? ""}
            onChange={(e) => setOldVer(e.target.value)}
          >
            <option value="" disabled>
              Select...
            </option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version}{v.is_original ? " (original)" : ""} — {formatDataDate(v.data_date) || v.filename.replace(/\.db$/, "")}
              </option>
            ))}
          </select>
        </label>
        <label className="diff-picker">
          <span>New</span>
          <select
            value={newVer ?? ""}
            onChange={(e) => setNewVer(e.target.value)}
          >
            <option value="" disabled>
              Select...
            </option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version}{v.is_original ? " (original)" : ""} — {formatDataDate(v.data_date) || v.filename.replace(/\.db$/, "")}
              </option>
            ))}
          </select>
        </label>

        <div className="diff-toolbar__actions">
          {diff && diff.roots.length > 0 && (
            <button
              className="diff-toolbar__btn"
              onClick={allExpanded ? handleCollapseAll : handleExpandAll}
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          )}
          {canRevert && diff && (diff.total_changed + diff.total_added + diff.total_removed) > 0 && (
            <button
              className="diff-toolbar__btn diff-toolbar__btn--danger"
              onClick={() => setShowRevertAllModal(true)}
            >
              Revert All
            </button>
          )}
        </div>
      </div>

      {loading && !diff && <div className="diff-loading">Loading diff...</div>}
      {error && <div className="diff-error">{error}</div>}

      {diff && (
        <>
          <div className="diff-summary">
            {diff.old_data_date && (
              <span className="diff-summary__dates">
                {diff.old_data_date.slice(0, 10)} → {diff.new_data_date?.slice(0, 10)}
              </span>
            )}
            <span className="diff-summary__pill diff-summary__pill--added">
              +{diff.total_added} added
            </span>
            <span className="diff-summary__pill diff-summary__pill--changed">
              {diff.total_changed} changed
            </span>
            <span className="diff-summary__pill diff-summary__pill--removed">
              -{diff.total_removed} removed
            </span>
            {(diff.wbs_added > 0 || diff.wbs_removed > 0 || diff.wbs_renamed > 0 || diff.wbs_moved > 0) && (
              <span className="diff-summary__wbs">
                WBS:
                {diff.wbs_added > 0 && <span className="diff-summary__pill diff-summary__pill--added">+{diff.wbs_added}</span>}
                {diff.wbs_removed > 0 && <span className="diff-summary__pill diff-summary__pill--removed">-{diff.wbs_removed}</span>}
                {diff.wbs_renamed > 0 && <span className="diff-summary__pill diff-summary__pill--changed">{diff.wbs_renamed} renamed</span>}
                {diff.wbs_moved > 0 && <span className="diff-summary__pill diff-summary__pill--moved">{diff.wbs_moved} moved</span>}
              </span>
            )}
          </div>

          <div className="diff-content">
            <DiffSidebar roots={diff.roots} onNavigate={handleNavigate} />
            <div className="diff-sections">
              {diff.roots.length === 0 ? (
                <div className="diff-empty">No differences found.</div>
              ) : (
                diff.roots.map((node) => (
                  <DiffTreeNode
                    key={node.wbs_path}
                    node={node}
                    depth={0}
                    expandedPaths={expandedPaths}
                    onToggle={handleToggle}
                    canRevert={canRevert}
                    reverting={reverting}
                    onRevertField={revertField}
                    onRevertActivity={revertActivity}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
      {showRevertAllModal && diff && (
        <RevertAllModal
          changedCount={diff.total_changed}
          onCancel={() => setShowRevertAllModal(false)}
          onConfirm={async () => {
            try {
              await revertAll();
              setShowRevertAllModal(false);
            } catch {
              // Modal stays open; error shown by useDiff
            }
          }}
          reverting={revertingAll}
        />
      )}
    </div>
  );
};

export default DiffView;
